/**
 * 🟦 V11.4 — Modal de confirmación de style inference.
 *
 * Muestra preview + nombre sugerido + descripción + lista de patches.
 * Botones: "Guardar como receta" / "Aplicar ahora" / "Cancelar".
 * Cierre por X, Cancelar, click-fuera, ESC.
 */
import { useEffect } from 'react';
import { X, Save, Wand2, Loader2 } from 'lucide-react';

export default function StyleInferenceModal({
  open, result, busy, onClose, onSaveRecipe, onApplyNow,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !result) return null;

  return (
    <div
      data-testid="lyon-style-inference-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[95] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
    >
      <div className="bg-slate-950 border border-fuchsia-700/40 rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-fuchsia-950/50 to-purple-950/40">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-fuchsia-300" /> Estilo inferido
            <span className="text-[10px] font-mono text-slate-400">· {result.source}</span>
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {result.preview && (
            <div className="rounded-lg overflow-hidden border border-slate-700/60 bg-slate-900 flex items-center justify-center p-3">
              <img
                src={`data:image/png;base64,${result.preview}`}
                alt="preview"
                data-testid="lyon-style-preview-img"
                className="max-h-48 object-contain"
              />
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase text-slate-500 tracking-wide">Nombre sugerido</p>
            <p className="text-base font-bold text-white font-mono" data-testid="lyon-style-name">
              {result.styleName}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-slate-500 tracking-wide">Descripción</p>
            <p className="text-sm text-slate-300">{result.description || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-slate-500 tracking-wide mb-1">
              Patches ({result.patches.length})
            </p>
            <ol className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {result.patches.map((p, i) => (
                <li key={i} className="text-[11px] font-mono text-slate-300 bg-slate-900/70 px-2 py-1 rounded border border-slate-800">
                  <span className="text-fuchsia-400">{p.op}</span> <span className="text-amber-300">{p.path}</span>
                  {p.value !== undefined && <span className="text-emerald-300"> = "{String(p.value).slice(0, 60)}"</span>}
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-end gap-2 bg-slate-950/80 flex-wrap">
          <button
            type="button" onClick={onClose} disabled={busy}
            data-testid="lyon-style-cancel"
            className="px-3 py-1.5 rounded-md text-xs text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-50"
          >Cancelar</button>
          <button
            type="button" onClick={onSaveRecipe} disabled={busy}
            data-testid="lyon-style-save-recipe"
            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50 flex items-center gap-1.5"
          ><Save className="w-3 h-3" /> Guardar como receta</button>
          <button
            type="button" onClick={onApplyNow} disabled={busy}
            data-testid="lyon-style-apply-now"
            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-50 flex items-center gap-1.5"
          >{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Aplicar ahora</button>
        </div>
      </div>
    </div>
  );
}
